import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMapStore } from '@/store/mapStore';
import { fetchMapPoints, fetchMapPointsCounts } from '@/services/api';
import { Layers, Map as MapIcon, Activity, AlertTriangle, Shield, X, ChevronLeft, ChevronRight, RefreshCw, PanelRight, Download, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useRole } from '@/store/userStore';

const LAYER_CONFIG = [
  { id: 'ops',        label: 'OPS',        color: '#4A9EFF', icon: Shield,        popup: (p) => `<div style="color:var(--vz-text-primary,#1e293b);font-size:12px"><b>${p.persona || '—'}</b><br/>Sede: ${p.sede_label || p.sede || '—'}<br/>Tipo: ${p.tipoops || '—'}<br/>Incumplimiento: ${p.incumplimiento || '—'}</div>` },
  { id: 'liderazgo',  label: 'Liderazgo',  color: '#00C853', icon: Activity,      popup: (p) => `<div style="color:var(--vz-text-primary,#1e293b);font-size:12px"><b>${p.persona || '—'}</b><br/>Sede: ${p.sede_label || p.sede || '—'}<br/>Actividad: ${p.actividad || '—'}<br/>Asistentes: ${p.numeroasistentes || '—'}</div>` },
  { id: 'near_misses',label: 'Near Misses',color: '#FF6B35', icon: AlertTriangle, popup: (p) => `<div style="color:var(--vz-text-primary,#1e293b);font-size:12px"><b>Near Miss</b><br/>Sede: ${p.centro_nombre || '—'}<br/>Categoría: ${p.categoria_nm || '—'}<br/>Gravedad: ${p.gravedad || '—'}<br/>Estado: ${p.estado || '—'}</div>` },
];

// ArcGIS sends raw catalog keys like "CAMINO_HORMIGUERAS" — humanize for display only
const prettyLabel = (v) => {
  if (!v) return v;
  const s = String(v).replace(/_/g, ' ').trim();
  return s.length > 3 && s === s.toUpperCase() ? s : s.charAt(0).toUpperCase() + s.slice(1);
};

function MapPanel({ onClose, selectedLayer, setSelectedLayer, showCluster, toggleCluster, pointCounts, isLoading, desde, hasta, geoTotals, mapPointsData }) {
  const cfg = LAYER_CONFIG.find(l => l.id === selectedLayer) || LAYER_CONFIG[0];
  // Top 5 centros for the CURRENTLY SELECTED layer — computed client-side from
  // the same points already loaded for the map, so it always shows real n>0
  // centros for OPS/Liderazgo/Near Misses instead of an OPS-only stat.
  const centroField = selectedLayer === 'near_misses' ? 'centro_nombre' : 'centro';
  const features = mapPointsData?.features || [];
  const grouped = {};
  for (const f of features) {
    // Records with no centro name (orphan Near Misses without centro_codigo,
    // see Admin > Sin Sede > NM) don't represent a real centro — skip them so
    // they never outrank an actual centro in the Top 5.
    const key = f[centroField] || f.sede_label || f.sede || null;
    if (!key) continue;
    if (!grouped[key]) grouped[key] = { centro: key, sede: f.sede_label, total_ops: 0 };
    grouped[key].total_ops += 1;
  }
  const topCentros = Object.values(grouped).sort((a, b) => b.total_ops - a.total_ops).slice(0, 5);
  const centrosLoading = isLoading;

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--vz-bg-card)', borderLeft: '1px solid var(--vz-border-subtle)' }} data-testid="arcgis-side-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--vz-border-subtle)' }}>
        <div className="flex items-center gap-2">
          <MapIcon size={16} style={{ color: 'var(--vz-green)' }} />
          <span className="font-montserrat text-sm font-semibold text-white">Panel del Mapa</span>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}><X size={14} style={{ color: 'var(--vz-text-muted)' }} /></Button>
      </div>

      <Tabs defaultValue="layers" className="flex-1 flex flex-col min-h-0">
        <TabsList className="mx-3 mt-3 grid grid-cols-3 h-8 text-xs" style={{ background: 'var(--vz-bg-elevated)' }}>
          <TabsTrigger value="layers" className="text-xs" data-testid="arcgis-tab-layers">Capas</TabsTrigger>
          <TabsTrigger value="legend" className="text-xs" data-testid="arcgis-tab-legend">Leyenda</TabsTrigger>
          <TabsTrigger value="details" className="text-xs" data-testid="arcgis-tab-details">Top 5</TabsTrigger>
        </TabsList>

        <TabsContent value="layers" className="flex-1 overflow-auto px-4 py-3 space-y-4">
          <div>
            <p className="font-montserrat text-[10px] uppercase tracking-wider mb-3" style={{ color: 'var(--vz-text-muted)' }}>Capa activa</p>
            <div className="space-y-2">
              {LAYER_CONFIG.map((layer) => {
                const count = pointCounts[layer.id] ?? geoTotals?.[layer.id] ?? '—';
                const total = geoTotals?.[layer.id];
                const badgeLabel = (pointCounts[layer.id] != null && total)
                  ? `${count} de ${total} con GPS`
                  : `${count} pts`;
                return (
                  <button key={layer.id} onClick={() => setSelectedLayer(layer.id)} className="w-full flex items-center gap-3 p-2.5 rounded-xl transition-colors duration-200"
                    style={{ background: selectedLayer === layer.id ? `${layer.color}15` : 'var(--vz-bg-elevated)', border: `1px solid ${selectedLayer === layer.id ? layer.color + '40' : 'var(--vz-border-subtle)'}` }}>
                    <layer.icon size={14} style={{ color: layer.color }} />
                    <span className="text-sm font-medium" style={{ color: selectedLayer === layer.id ? layer.color : 'var(--vz-text-secondary)' }}>{layer.label}</span>
                    {isLoading && selectedLayer === layer.id ? (
                      <div className="ml-auto w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: layer.color }} />
                    ) : (
                      <Badge className="ml-auto text-[10px] font-mono-custom" style={{ background: `${layer.color}20`, color: layer.color, border: `1px solid ${layer.color}30` }}>
                        {badgeLabel}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-3">
            <p className="font-montserrat text-[10px] uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>Opciones</p>
            <div className="flex items-center justify-between">
              <Label className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>Agrupar puntos</Label>
              <Switch checked={showCluster} onCheckedChange={toggleCluster} />
            </div>
            <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>
              Solo se muestran registros con coordenadas GPS. Los registros sin ubicación no aparecen en el mapa.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="legend" className="flex-1 overflow-auto px-4 py-3">
          <div className="space-y-3">
            {LAYER_CONFIG.map((layer) => (
              <div key={layer.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--vz-bg-elevated)' }}>
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: layer.color }} />
                <div>
                  <span className="text-sm font-medium" style={{ color: 'var(--vz-text-secondary)' }}>{layer.label}</span>
                  <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>
                    {pointCounts[layer.id] != null
                      ? `${pointCounts[layer.id]}${geoTotals?.[layer.id] ? ` de ${geoTotals[layer.id]} con GPS` : ' puntos con GPS'}`
                      : geoTotals?.[layer.id] != null ? `${geoTotals[layer.id]} registros (selecciona para ver GPS)` : '— puntos con GPS'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="details" className="flex-1 overflow-auto px-4 py-3">
          <p className="font-montserrat text-[10px] uppercase tracking-wider mb-3" style={{ color: 'var(--vz-text-muted)' }}>Top 5 centros — {cfg.label}</p>
          {centrosLoading ? (
            <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-10 rounded-xl animate-pulse" style={{ background: 'var(--vz-bg-elevated)' }} />)}</div>
          ) : topCentros.length === 0 ? (
            <p className="text-xs py-8 text-center" style={{ color: 'var(--vz-text-muted)' }}>
              Sin registros con GPS para {cfg.label} en este periodo.
            </p>
          ) : (
            <div className="space-y-2">
              {topCentros.map((centro, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
                  <div className="flex items-center gap-2">
                    <span className="font-montserrat text-xs font-bold" style={{ color: '#00C853' }}>#{i + 1}</span>
                    <div>
                      <p className="text-xs font-medium truncate max-w-[140px]" style={{ color: 'var(--vz-text-primary)' }}>{prettyLabel(centro.centro || centro.sede) || 'Sin nombre'}</p>
                      {centro.sede && <p className="text-[10px] truncate max-w-[140px]" style={{ color: 'var(--vz-text-muted)' }}>{prettyLabel(centro.sede)}</p>}
                    </div>
                  </div>
                  <span className="font-bold tabular-nums text-sm" style={{ color: '#4A9EFF' }}>{centro.total_ops}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function Mapa() {
  const mapRef    = useRef(null);
  const viewRef   = useRef(null);
  const graphicsLayerRef = useRef(null);
  const mapContainerRef  = useRef(null);
  const selectedLayerRef = useRef(null);
  const [mapLoaded,  setMapLoaded]  = useState(false);
  const [mapError,   setMapError]   = useState(null);
  const [panelOpen,  setPanelOpen]  = useState(true);
  const [pointCounts, setPointCounts] = useState({});
  const [geoTotals, setGeoTotals] = useState({});
  const [year, setYear] = useState(new Date().getFullYear());
  const desde = `${year}-01-01`;
  const hasta = `${year}-12-31`;
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, items: [], count: 0, layerId: null });
  const [isMobilePanel, setIsMobilePanel] = useState(false);
  const { selectedLayer, showCluster, setSelectedLayer, toggleCluster } = useMapStore();
  const { canExport } = useRole();

  // Keep ref in sync for stable stale-closure access inside map event handler
  useEffect(() => { selectedLayerRef.current = selectedLayer; }, [selectedLayer]);

  // Detect mobile/tablet viewport
  useEffect(() => {
    const check = () => setIsMobilePanel(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Fetch map points for the currently selected layer
  const { data: mapPointsData, isLoading: pointsLoading, refetch: refetchPoints } = useQuery({
    queryKey: ['map-points', selectedLayer, desde, hasta],
    queryFn: () => fetchMapPoints(selectedLayer, 2000, { desde, hasta }),
    staleTime: 5 * 60 * 1000,
    enabled: mapLoaded,
  });

  // Lightweight RLS-aware counts for ALL layers (year-filtered universe, not
  // restricted to valid GPS coords) — the denominator for "N de M con GPS".
  const { data: allLayerCounts } = useQuery({
    queryKey: ['map-points-counts', desde, hasta],
    queryFn: () => fetchMapPointsCounts({ desde, hasta }),
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => { if (allLayerCounts) setGeoTotals(allLayerCounts); }, [allLayerCounts]);
  useEffect(() => { setPointCounts({}); }, [year]);

  // Initialize ArcGIS map (once, no auth needed – we use GraphicsLayer)
  useEffect(() => {
    let isMounted = true;

    const initMap = async () => {
      try {
        const [
          { default: Map },
          { default: MapView },
          { default: GraphicsLayer },
          { default: TileLayer },
          { default: Basemap },
        ] = await Promise.all([
          import('@arcgis/core/Map'),
          import('@arcgis/core/views/MapView'),
          import('@arcgis/core/layers/GraphicsLayer'),
          import('@arcgis/core/layers/TileLayer'),
          import('@arcgis/core/Basemap'),
        ]);
        if (!isMounted) return;

        // Esri's classic "World Dark Gray Base" raster tile service — public,
        // no API key required (unlike the newer vector basemap styles, which
        // is why CARTO's dark_all basemap started showing an "API KEY
        // REQUIRED" watermark once they locked down anonymous tile access).
        const darkGrayLayer = new TileLayer({
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer',
          copyright: '© Esri, HERE, Garmin, OpenStreetMap contributors',
        });
        const basemap = new Basemap({ baseLayers: [darkGrayLayer], title: 'Dark', id: 'dark' });
        const gLayer  = new GraphicsLayer({ id: 'data-layer' });
        graphicsLayerRef.current = gLayer;

        const map = new Map({ basemap, layers: [gLayer] });
        const view = new MapView({
          container: mapRef.current,
          map,
          center: [-3.7, 40.4],
          zoom: 6,
          ui: { components: ['zoom', 'compass'] },
          popup: { dockEnabled: false },
        });
        viewRef.current = view;
        await view.when();
        if (isMounted) {
          setMapLoaded(true);
          // Cluster tooltip on hover
          view.on('pointer-move', async (evt) => {
            if (!graphicsLayerRef.current) return;
            try {
              const resp = await view.hitTest(evt, { include: [graphicsLayerRef.current] });
              const hit = resp.results.find(r => r.type === 'graphic' && r.graphic?.attributes != null);
              if (hit) {
                const g = hit.graphic;
                const count = g.attributes.count ?? 1;
                const items = g.attributes.tooltipItems || [g.attributes];
                const containerW = mapContainerRef.current?.clientWidth || 800;
                const flipX = evt.x > containerW * 0.65;
                setTooltip({
                  visible: true,
                  x: flipX ? evt.x - 296 : evt.x + 14,
                  y: Math.max(0, evt.y - 10),
                  items: items,
                  count: count,
                  layerId: selectedLayerRef.current,
                });
              } else {
                setTooltip(prev => prev.visible ? { ...prev, visible: false } : prev);
              }
            } catch { /* ignore */ }
          });
        }
      } catch (err) {
        console.error('ArcGIS map error:', err);
        if (isMounted) setMapError(err.message);
      }
    };

    initMap();
    return () => {
      isMounted = false;
      viewRef.current?.destroy();
      viewRef.current = null;
    };
  }, []); // run once

  // Render points into the GraphicsLayer when data or showCluster changes
  useEffect(() => {
    if (!mapLoaded || !graphicsLayerRef.current || !mapPointsData?.features) return;

    const renderPoints = async () => {
      try {
        const [
          { default: Graphic },
          { default: Point },
          { default: SimpleMarkerSymbol },
          { default: TextSymbol },
        ] = await Promise.all([
          import('@arcgis/core/Graphic'),
          import('@arcgis/core/geometry/Point'),
          import('@arcgis/core/symbols/SimpleMarkerSymbol'),
          import('@arcgis/core/symbols/TextSymbol'),
        ]);

        const cfg = LAYER_CONFIG.find(l => l.id === selectedLayer) || LAYER_CONFIG[0];
        const features = mapPointsData.features;

        // Track counts
        setPointCounts(prev => ({ ...prev, [selectedLayer]: features.length }));

        graphicsLayerRef.current.removeAll();

        // Group points for client-side clustering when showCluster is true
        if (showCluster && features.length > 0) {
          // Build a simple grid-based clustering
          const clusterMap = {};
          const cellSize = 0.5; // degrees (approx 50km)
          for (const f of features) {
            const cx = Math.round(f.x / cellSize);
            const cy = Math.round(f.y / cellSize);
            const key = `${cx},${cy}`;
            if (!clusterMap[key]) clusterMap[key] = { items: [], cx: cx * cellSize, cy: cy * cellSize };
            clusterMap[key].items.push(f);
          }

          const circleGraphics = Object.values(clusterMap).map(cluster => {
            const count = cluster.items.length;
            const isSingle = count === 1;
            const size = isSingle ? 10 : Math.min(8 + Math.sqrt(count) * 4, 48);
            const alpha = isSingle ? 'CC' : 'DD';
            return new Graphic({
              geometry: new Point({ longitude: cluster.cx, latitude: cluster.cy }),
              symbol: new SimpleMarkerSymbol({
                style: 'circle',
                color: cfg.color + alpha,
                size: `${size}px`,
                outline: { color: isSingle ? '#06152E' : '#FFFFFFA0', width: isSingle ? 1 : 2 },
              }),
              attributes: { count, tooltipItems: cluster.items.slice(0, 5), ...cluster.items[0] },
              popupTemplate: {
                title: isSingle ? cfg.label : `${cfg.label} — ${count} registros`,
                content: isSingle
                  ? cfg.popup(cluster.items[0])
                  : `Agrupa ${count} registros en esta zona. Acerca el mapa para ver detalles.`,
              },
            });
          });

          // Label graphics showing count for multi-point clusters
          const labelGraphics = Object.values(clusterMap)
            .filter(cluster => cluster.items.length > 1)
            .map(cluster => new Graphic({
              geometry: new Point({ longitude: cluster.cx, latitude: cluster.cy }),
              symbol: new TextSymbol({
                text: String(cluster.items.length),
                color: '#FFFFFF',
                haloColor: '#06152E',
                haloSize: '1.5px',
                font: { size: 9, weight: 'bold', family: 'sans-serif' },
              }),
            }));

          graphicsLayerRef.current.addMany([...circleGraphics, ...labelGraphics]);
        } else {
          // Individual points
          const graphics = features.map(f => new Graphic({
            geometry: new Point({ longitude: f.x, latitude: f.y }),
            symbol: new SimpleMarkerSymbol({
              style: 'circle',
              color: cfg.color + 'CC',
              size: '10px',
              outline: { color: '#06152E', width: 1.5 },
            }),
            attributes: f,
            popupTemplate: { title: cfg.label, content: cfg.popup(f) },
          }));
          graphicsLayerRef.current.addMany(graphics);
        }
      } catch (err) {
        console.error('Graphics render error:', err);
      }
    };

    renderPoints();
  }, [mapLoaded, mapPointsData, selectedLayer, showCluster]); // eslint-disable-line

  // CSV export of currently loaded map points
  const exportMapCSV = useCallback(() => {
    const features = mapPointsData?.features;
    if (!features?.length) return;
    const cfg = LAYER_CONFIG.find(l => l.id === selectedLayer) || LAYER_CONFIG[0];
    const skipKeys = new Set(['tooltipItems', '_synced_at', '_layer', 'OBJECTID', 'objectid']);
    const keys = Object.keys(features[0]).filter(k => !skipKeys.has(k));
    const header = ['Latitud', 'Longitud', ...keys];
    const rows = features.map(f => [
      (f.y?.toFixed(6) ?? ''),
      (f.x?.toFixed(6) ?? ''),
      ...keys.map(k => `"${(f[k] ?? '').toString().replace(/"/g, '""')}"`)
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mapa_${cfg.label.toLowerCase().replace(/ /g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [mapPointsData, selectedLayer]);

  // Excel export of currently loaded map points
  const exportMapXLSX = useCallback(async () => {
    const features = mapPointsData?.features;
    if (!features?.length) return;
    const cfg = LAYER_CONFIG.find(l => l.id === selectedLayer) || LAYER_CONFIG[0];
    const skipKeys = new Set(['tooltipItems', '_synced_at', '_layer', 'OBJECTID', 'objectid']);
    const keys = Object.keys(features[0]).filter(k => !skipKeys.has(k));
    const rows = features.map(f => {
      const row = { Latitud: f.y ?? '', Longitud: f.x ?? '' };
      keys.forEach(k => { row[k] = f[k] ?? ''; });
      return row;
    });
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, cfg.label.slice(0, 31));
    XLSX.writeFile(wb, `mapa_${cfg.label.toLowerCase().replace(/ /g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }, [mapPointsData, selectedLayer]);

  return (
    <div className="flex h-full" data-testid="mapa-page">
      <div className="flex-1 relative" ref={mapContainerRef}>
        <div ref={mapRef} className="w-full h-full" data-testid="arcgis-map" />

        {/* Cluster hover tooltip */}
        {tooltip.visible && tooltip.items.length > 0 && (() => {
          const cfg = LAYER_CONFIG.find(l => l.id === tooltip.layerId) || LAYER_CONFIG[0];
          return (
            <div
              className="absolute z-20 pointer-events-none rounded-xl border text-xs"
              style={{
                left: tooltip.x,
                top: tooltip.y,
                maxWidth: 280,
                background: 'rgba(8,20,52,0.97)',
                borderColor: cfg.color + '40',
                backdropFilter: 'blur(12px)',
                boxShadow: `0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px ${cfg.color}20`,
              }}
              data-testid="cluster-tooltip"
            >
              <div className="px-3 pt-3 pb-2 border-b" style={{ borderColor: cfg.color + '25' }}>
                <p className="font-montserrat font-semibold" style={{ color: cfg.color }}>
                  {cfg.label}
                  <span className="ml-1.5 font-mono text-[10px] opacity-70">({tooltip.count} registros)</span>
                </p>
              </div>
              <div className="px-3 py-2 space-y-2">
                {tooltip.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <span className="text-[9px] font-bold mt-0.5 flex-shrink-0" style={{ color: cfg.color + 'AA' }}>{i + 1}.</span>
                    <div className="text-[11px] leading-snug" style={{ color: 'var(--vz-text-secondary)' }}
                      dangerouslySetInnerHTML={{ __html: cfg.popup(item) }} />
                  </div>
                ))}
              </div>
              {tooltip.count > 5 && (
                <div className="px-3 pb-2.5">
                  <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>
                    +{tooltip.count - 5} registros más — acerca el mapa para ver.
                  </p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Layer selector overlay */}
        <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-2 rounded-xl border"
          style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', backdropFilter: 'blur(12px)' }}>
          {LAYER_CONFIG.map((l) => (
            <button key={l.id} onClick={() => setSelectedLayer(l.id)}
              className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg transition-colors duration-200"
              style={{ background: selectedLayer === l.id ? `${l.color}20` : 'transparent', color: selectedLayer === l.id ? l.color : '#546E7A' }}>
              <div className="w-2 h-2 rounded-full" style={{ background: l.color }} />
              {l.label}
              {pointCounts[l.id] != null && (
                <span className="font-mono-custom text-[9px] opacity-70">
                  ({pointCounts[l.id]}{geoTotals?.[l.id] && geoTotals[l.id] !== pointCounts[l.id] ? ` de ${geoTotals[l.id]}` : ''})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Year filter chips — same 3 years as OPS/Liderazgo/NM */}
        <div className="absolute z-10 flex items-center gap-1 px-2 py-1.5 rounded-xl border"
          style={{ top: '4.2rem', left: '1rem', background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', backdropFilter: 'blur(12px)' }}
          data-testid="mapa-year-chips">
          {[2024, 2025, 2026].map((y) => (
            <button key={y} onClick={() => setYear(y)}
              className="text-xs px-2.5 py-1 rounded-lg font-medium transition-colors duration-200"
              style={{ background: year === y ? 'var(--vz-accent-blue, #4A9EFF)' : 'transparent', color: year === y ? '#fff' : 'var(--vz-text-muted)' }}
              data-testid={`mapa-year-chip-${y}`}>
              {y}
            </button>
          ))}
        </div>

        {/* Refresh button */}
        <button onClick={() => refetchPoints()}
          className="absolute top-4 right-36 z-10 w-8 h-8 rounded-lg border flex items-center justify-center transition-colors duration-200"
          style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}>
          <RefreshCw size={13} className={pointsLoading ? 'animate-spin' : ''} />
        </button>

        {/* Toggle panel */}
        <button onClick={() => setPanelOpen(!panelOpen)}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-lg border flex items-center justify-center transition-colors duration-200"
          style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}
          data-testid="map-panel-toggle"
        >
          {isMobilePanel
            ? <PanelRight size={14} />
            : panelOpen ? <ChevronRight size={14} /> : <ChevronLeft size={14} />
          }
        </button>

        {/* CSV export button */}
        {canExport && (
          <button
            onClick={exportMapCSV}
            disabled={!mapPointsData?.features?.length}
            title={mapPointsData?.features?.length ? `Exportar ${mapPointsData.features.length} puntos a CSV` : 'Sin datos cargados'}
            className="absolute top-4 right-14 z-10 w-8 h-8 rounded-lg border flex items-center justify-center transition-colors duration-200 disabled:opacity-40"
            style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}
            data-testid="map-export-csv-btn"
          >
            <Download size={13} />
          </button>
        )}

        {/* Excel export button */}
        {canExport && (
          <button
            onClick={exportMapXLSX}
            disabled={!mapPointsData?.features?.length}
            title={mapPointsData?.features?.length ? `Exportar ${mapPointsData.features.length} puntos a Excel` : 'Sin datos cargados'}
            className="absolute top-4 right-24 z-10 w-8 h-8 rounded-lg border flex items-center justify-center transition-colors duration-200 disabled:opacity-40"
            style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', color: '#00C853' }}
            data-testid="map-export-xlsx-btn"
          >
            <FileSpreadsheet size={13} />
          </button>
        )}

        {/* Loading overlay */}
        {(!mapLoaded && !mapError) && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(11,26,62,0.85)' }}>
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-2 border-[#00C853] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm" style={{ color: 'var(--vz-text-secondary)' }}>Cargando mapa…</p>
            </div>
          </div>
        )}
        {mapError && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(11,26,62,0.9)' }}>
            <div className="text-center p-6 max-w-sm">
              <MapIcon size={32} className="mx-auto mb-3" style={{ color: '#FF5252' }} />
              <p className="text-sm font-medium text-white mb-1">Error al cargar el mapa</p>
              <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{mapError}</p>
            </div>
          </div>
        )}

        {/* Points loading indicator */}
        {mapLoaded && pointsLoading && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ background: 'rgba(15,32,69,0.9)', border: '1px solid var(--vz-border-subtle)' }}>
            <div className="w-3 h-3 border-2 border-[#00C853] border-t-transparent rounded-full animate-spin" />
            <span className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>Cargando puntos…</span>
          </div>
        )}
      </div>

      {/* Desktop side panel (lg+) */}
      {!isMobilePanel && panelOpen && (
        <div className="w-80 flex-shrink-0 flex flex-col">
          <MapPanel
            onClose={() => setPanelOpen(false)}
            selectedLayer={selectedLayer}
            setSelectedLayer={setSelectedLayer}
            showCluster={showCluster}
            toggleCluster={toggleCluster}
            pointCounts={pointCounts}
            geoTotals={geoTotals}
            isLoading={pointsLoading}
            desde={desde}
            hasta={hasta}
            mapPointsData={mapPointsData}
          />
        </div>
      )}

      {/* Mobile/tablet sheet panel (< lg) */}
      {isMobilePanel && (
        <Sheet open={panelOpen} onOpenChange={(open) => !open && setPanelOpen(false)}>
          <SheetContent
            side="right"
            className="w-80 p-0 border-l"
            style={{ background: 'var(--vz-bg-primary)', borderColor: 'var(--vz-border-subtle)' }}
            data-testid="map-panel-sheet"
          >
            <MapPanel
              onClose={() => setPanelOpen(false)}
              selectedLayer={selectedLayer}
              setSelectedLayer={setSelectedLayer}
              showCluster={showCluster}
              toggleCluster={toggleCluster}
              pointCounts={pointCounts}
              geoTotals={geoTotals}
              isLoading={pointsLoading}
              desde={desde}
              hasta={hasta}
              mapPointsData={mapPointsData}
            />
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
